/**
 * Control Redux Slice
 *
 * Simplified state management for controls.
 *
 * @module entities/control/model/controlSlice
 */

import { createSlice, createEntityAdapter } from '@reduxjs/toolkit';
import { controlApi } from '../api/controlApi';

const getControlEntityId = (control) => control?.control_id || control?.id || control?.name || '';

const getSortableControlValue = (control) =>
  String(control?.control_id || control?.name || control?.id || '').toLowerCase();

export const controlAdapter = createEntityAdapter({
  selectId: getControlEntityId,
  sortComparer: (a, b) => getSortableControlValue(a).localeCompare(getSortableControlValue(b)),
});

const initialState = controlAdapter.getInitialState({
  loading: false,
  error: null,
  filters: {
    search: '',
    framework: 'all',
    type: 'all',
    status: 'all',
    owner: null,
    effectiveness: 'all',
  },
  sortBy: 'control_id',
  sortOrder: 'asc',
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
  },
  selectedControlId: null,
  viewMode: 'grid',
});

export const controlSlice = createSlice({
  name: 'controls',
  initialState,
  reducers: {
    setSearchFilter: (state, action) => {
      state.filters.search = action.payload;
      state.pagination.page = 1;
    },
    setFrameworkFilter: (state, action) => {
      state.filters.framework = action.payload;
      state.pagination.page = 1;
    },
    setTypeFilter: (state, action) => {
      state.filters.type = action.payload;
      state.pagination.page = 1;
    },
    setStatusFilter: (state, action) => {
      state.filters.status = action.payload;
      state.pagination.page = 1;
    },
    setOwnerFilter: (state, action) => {
      state.filters.owner = action.payload;
      state.pagination.page = 1;
    },
    setEffectivenessFilter: (state, action) => {
      state.filters.effectiveness = action.payload;
      state.pagination.page = 1;
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.page = 1;
    },
    setSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (state, action) => {
      state.sortOrder = action.payload;
    },
    toggleSortOrder: (state) => {
      state.sortOrder = state.sortOrder === 'asc' ? 'desc' : 'asc';
    },
    setPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setPageLimit: (state, action) => {
      state.pagination.limit = action.payload;
      state.pagination.page = 1;
    },
    nextPage: (state) => {
      if (state.pagination.page < Math.ceil(state.pagination.total / state.pagination.limit))
        state.pagination.page += 1;
    },
    previousPage: (state) => {
      if (state.pagination.page > 1) state.pagination.page -= 1;
    },
    setSelectedControl: (state, action) => {
      state.selectedControlId = action.payload;
    },
    clearSelectedControl: (state) => {
      state.selectedControlId = null;
    },
    setViewMode: (state, action) => {
      state.viewMode = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(controlApi.endpoints.getControls.matchFulfilled, (state, action) => {
      controlAdapter.setAll(state, action.payload.data);
      state.pagination.total = action.payload.total || action.payload.data.length;
      state.loading = false;
    });
    builder.addMatcher(controlApi.endpoints.getControls.matchPending, (state) => {
      state.loading = true;
    });
  },
});

export const {
  setSearchFilter,
  setFrameworkFilter,
  setTypeFilter,
  setStatusFilter,
  setOwnerFilter,
  setEffectivenessFilter,
  clearFilters,
  setSortBy,
  setSortOrder,
  toggleSortOrder,
  setPage,
  setPageLimit,
  nextPage,
  previousPage,
  setSelectedControl,
  clearSelectedControl,
  setViewMode,
} = controlSlice.actions;

export const controlSelectors = controlAdapter.getSelectors((state) => state.controls);
export const {
  selectAll: selectAllControls,
  selectById: selectControlById,
  selectIds: selectControlIds,
  selectEntities: selectControlEntities,
  selectTotal: selectTotalControls,
} = controlSelectors;

export const selectControlsLoading = (state) => state.controls.loading;
export const selectControlsError = (state) => state.controls.error;
export const selectControlFilters = (state) => state.controls.filters;
export const selectControlSort = (state) => ({
  sortBy: state.controls.sortBy,
  sortOrder: state.controls.sortOrder,
});
export const selectControlPagination = (state) => state.controls.pagination;
export const selectControlViewMode = (state) => state.controls.viewMode;
export const selectSelectedControlId = (state) => state.controls.selectedControlId;
export const selectSelectedControl = (state) =>
  state.controls.selectedControlId
    ? selectControlEntities(state)[state.controls.selectedControlId]
    : null;
export const selectFilteredControls = (state) => selectAllControls(state);
export const selectPaginatedControls = (state) => selectAllControls(state);
export const selectControlsByFramework = (state) => selectAllControls(state);
export const selectControlsByType = (state) => selectAllControls(state);
export const selectControlsByStatus = (state) => selectAllControls(state);
export const selectIneffectiveControls = (state) =>
  selectAllControls(state).filter((c) => c.effectiveness_rating < 2);
export const selectControlsNeedingAttestation = (state) => selectAllControls(state);
export const selectControlsNeedingTesting = (state) => selectAllControls(state);
export const selectComputedStatistics = (state) => ({ total: selectAllControls(state).length });
export const selectHasActiveFilters = (state) =>
  state.controls.filters.search !== '' || state.controls.filters.framework !== 'all';

export default controlSlice.reducer;
